/* globals __DEV__ */
/** @jsxImportSource @emotion/react */
import React, { Fragment } from 'react'
import { Global } from '@emotion/react'
import css from '@emotion/css/macro'
import PropTypes from 'prop-types'
import {
  matchPath,
  Route,
  Redirect,
  Switch,
  withRouter,
} from 'react-router-dom'
import { connect } from 'react-redux'
import { ConnectedRouter } from 'connected-react-router'
import { clearAllBodyScrollLocks } from 'body-scroll-lock'
import {
  removeLogoutSignal,
  LOGOUT_SIGNAL,
  SN_STORAGE_EVENT_LISTENER,
  setIframeSessionToken,
} from '@sportninja/common/api/account'
import { AUTHENTICATED_ROUTES, ROUTES } from '@sportninja/common/constants/app'
import userActions from '@sportninja/common/actions/user'
import typeActions from '@sportninja/common/actions/types'
import { bindActionToPromise } from '@sportninja/common/actions/utils'
import authActions from '@sportninja/common/actions/auth'
import i18n, { t } from '@sportninja/common/i18n'
import * as Sentry from '@sentry/react'
import appOrigin from '@sportninja/common/constants/appOrigin'
import notificationsActions from '@sportninja/common/actions/notifications'
import queryString from 'query-string'
import { getSelfUser } from '@sportninja/common/selectors/users'
import { queryClient } from '@sportninja/common/config/queryClient'

// Components
import MainNav from '../components/MainNav'
import LoadingSpinner from '../components/LoadingSpinner'
import PrivateRoute from '../components/PrivateRoute'
import ScrollToTop from '../components/ScrollToTop'
import BannerContainer from '../components/Banner/BannerContainer'
import ErrorBoundary from '../components/ErrorBoundary'

import RegisterSuccess from './Register/RegisterSuccess'
import ForgotPassword from './ForgotPassword'
import VerifyEmail from './VerifyEmail'
import ResetPassword from './ResetPassword'

import { Login } from './Onboarding/Login'
import { SignUp } from './Onboarding/SignUp'

// Routes
import RegistrationPreview from './RegistrationPreview'
import Sandbox from './Sandbox'
import NoMatch from './NoMatch'
import Profile from './Profile/index.tsx'
import Game from './Game'
import Teams from './Teams'
import Team from './Team'
import Organizations from './Orgs'
import Org from './Org'
import Schedules from './Schedules'
import Schedule from './Schedule'
// import User from './User'
import InviteV2 from './InviteV2'
import InviteRegister from './InviteV2/InviteRegister'
// import Test from './Test'
// Old player page (kept for rollback): import Player from './PlayerV2/index.jsx'
import Player from './Player'
import Scoresheets from './Scoresheets'
import ScoresheetsFirefox from './ScoresheetsFirefox'
import MergePlayers from './MergePlayers'
import MergeTeams from './MergeTeams'
import Home from './Home'
import Following from './Following'

import { font } from '../components/css'
import FrameStyles from './FrameStyles'
import ErrorController from '../components/ErrorController'
import OutdatedHandler from '../components/OutdatedHandler'
import { RECENTS_KEY_NAME } from './utils'
import Search from './Search'
import ScheduleReprocessingContextProvider from '../components/ScheduleReprocessingContext'
import PusherContextProvider from './PusherContext'
import { GameChannelProvider } from '../context/GameChannelContext'
import SearchContextProvider from './Search/SearchContextProvider'
import { getIframeToken } from '@sportninja/common/utils/iframe-tokens-utils'
import RegistrationPayment from './RegistrationPayment'
import RegistrationInvite from './RegistrationInviteV2'
import { GeneralSettings } from './GeneralSettings'
import { DownloadApp } from 'src/components/DownloadApp'
import { setInviteSports } from '@sportninja/common/actions/registrationInvite'
import RegistrationContextWrapper from './RegistrationInviteV2/context/RegistrationContext'
import SignInReportsPrint from './SignInReportsPrint'
import SignInReportsPrintFirefox from './SignInReportsPrintFirefox'
import Registrations from './Registrations'
import RegistrationDetails from './RegistrationDetails'
import { getAllGenericSportProperties } from '@sportninja/common/selectors/types'
import PlayoffMobile from './PlayoffMobile'
import MySchedule from './MySchedule'
import MyMembership from './MyMembership'
import ComingSoon from './ComingSoon'
import Communications from './Communications'
import Locations from './Locations'
import HighlightRedirect from './HighlightRedirect'
import { Layout } from 'antd'
import Sidebar from '../components/Sidebar'
import colors from '@sportninja/common/constants/appColors'
import { dispatchSidebarToggle } from 'src/hooks/useSidebarState'

const SORT_STATE_STORAGE_KEY = 'sn:sortstate'
const SIDEBAR_COLLAPSED_KEY = 'sn:sidebar-collapsed'

// Only display the header and burger menu on authenticated routes
// TODO: SOME BETTER WAY OF AUTOMATING THIS, SO YOU DON'T FORGET EVERY TIME
const re =
  /\/users|invitations|game|team|org|schedule|user|player|highlight|feed|registration-details\//i
const authenticatedRoute = (path) => {
  const r1 = AUTHENTICATED_ROUTES.includes(path)
  const r2 = path.search(re) !== -1
  return r1 || r2
}

export const FrameContext = React.createContext(undefined)
export const TableSortContext = React.createContext(undefined)

// Consult the web/canlan_widget_embed_example.html file for what needs to be located on Canlan's ASHL site
const _FramePageListener = ({ history, location }) => {
  // We need a lock to prevent propagating location changes to the child when the
  // child is going back in history.
  const lock = React.useRef(null)

  // The domain that is hosting this widget instance, i.e. ASHL, CloudFront, etc.
  const targetOrigin = appOrigin
  // const targetOrigin = 'http://localhost:5000'

  // eslint-disable-next-line no-shadow
  const sendMessage = React.useCallback((location, title) => {
    const message = JSON.stringify({ title, location })
    const ancestorOrigin =
      document.location.ancestorOrigins && document.location.ancestorOrigins[0]
    if (targetOrigin === ancestorOrigin) {
      parent.postMessage(message, targetOrigin)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  React.useEffect(() => {
    const handler = (event) => {
      if (event.origin !== targetOrigin) {
        return
      }
      const payload = event.data
      if (payload === 'popstate') {
        lock.current = true
        history.goBack()
      }
    }

    window.addEventListener('message', handler)

    return () => {
      window.removeEventListener('message', handler)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  React.useEffect(() => {
    setTimeout(() => {
      if (lock.current) {
        lock.current = false
        return
      }
      sendMessage(location, document.title)
      // Wait a bit, so that the title can be updated properly.
    }, 300)
  }, [location, sendMessage])

  return <div />
}

_FramePageListener.propTypes = {
  history: PropTypes.object,
  location: PropTypes.object,
}

const FramePageListener = withRouter(_FramePageListener)

class App extends React.Component {
  constructor(props) {
    super(props)

    let sortState = {}
    try {
      const storedSortState = localStorage.getItem(SORT_STATE_STORAGE_KEY)
      if (typeof storedSortState === 'string') {
        sortState = JSON.parse(storedSortState)
      }
    } catch (e) {
      console.error('Problem parsing local storage')
    }

    let sidebarCollapsed = false
    let hasSavedSidebarPreference = false
    try {
      const storedSidebarState = localStorage.getItem(SIDEBAR_COLLAPSED_KEY)
      if (storedSidebarState !== null) {
        sidebarCollapsed = JSON.parse(storedSidebarState)
        hasSavedSidebarPreference = true
      }
    } catch (e) {
      console.error('Problem parsing sidebar state from local storage')
    }

    const decodedHash = decodeURIComponent(window.location.hash)
    const parsedHash = queryString.parse(decodedHash)
    let config = {}
    if (typeof parsedHash?.config === 'string') {
      try {
        config = JSON.parse(parsedHash.config)
      } catch (e) {
        console.error('Problem parsing query string')
      }
    }

    this.state = {
      failed: false,
      loading: true,
      sortState,
      sidebarCollapsed,
      hasSavedSidebarPreference,
      // inFrame: true,
      inFrame: window.location !== window.parent.location,
      outdated: false,
      frameConfig: config,
      isOnOrg: this.props?.history?.location?.pathname?.includes('org'),
      isOnTeam: this.props?.history?.location?.pathname?.includes('team'),
      isOnSchedule:
        this.props?.history?.location?.pathname?.includes('schedule'),
      isOnRegistration:
        this.props?.history?.location?.pathname?.includes('forgot-password') ||
        (this.props?.history?.location?.pathname?.includes('registration') &&
          !this.props?.history?.location?.pathname?.includes(
            'registrations'
          )) ||
        this.props?.history?.location?.pathname?.includes('register') ||
        this.props?.history?.location?.pathname?.includes('pay') ||
        this.props?.history?.location?.pathname?.includes('invitations') ||
        this.props?.history?.location?.pathname?.includes(
          'password-reset-registration'
        ) ||
        this.props?.history?.location?.search?.includes('isRegistration'),
      isOnPlayoffMobile:
        this.props?.history?.location?.pathname?.includes('playoffs'),
    }
  }

  async componentDidMount() {
    this.props.setInFrame(this.state.inFrame)

    // Add keyboard shortcut listener for sidebar toggle
    window.addEventListener('keydown', this.handleSidebarShortcut)

    await i18n.init()
    this.loadGA()

    if (this.state.inFrame) {
      try {
        // Try to refresh with whatever token is available
        await this.props.refresh()
      } catch (e) {
        await setIframeSessionToken(getIframeToken())
        try {
          // Try once more
          await this.props.refresh()
        } catch (_e) {}
      }
    } else {
      try {
        await this.props.refresh()
      } catch (e) {}
    }
    try {
      this.props.history.listen((location) => {
        this.setState({
          isOnSchedule: location.pathname.includes('schedule'),
          isOnTeam: location.pathname.includes('team'),
          isOnOrg: location.pathname.includes('org'),
          isOnRegistration:
            location.pathname.includes('forgot-password') ||
            (location.pathname.includes('registration') &&
              !location.pathname.includes('registrations')) ||
            location.pathname.includes('register') ||
            location.pathname.includes('pay') ||
            location.pathname.includes('invitations') ||
            location.search.includes('isRegistration'),
        })
      })
    } catch (e) {
      console.error('Problem with history listener', e)
      this.setState({ isOnRegistration: false })
    }
    try {
      // Do this regardless of refresh success, since we won't get another chance
      // if the user logs in later in the same instance
      // Let's keep this for a while until we are 100% sure nothing is breaking
      // const { data } = await this.props.requestSports()
      // const hockey = data.find((d) => d.name === 'hockey')
      // await this.props.requestTypesBySport(hockey.id)
      // Now we read all the sports instead of each one individually
      await this.props.requestSports()
      const res = await this.props.requestTypesAllSports()
      this.props.dispatchSetInviteSports(res)
      // Fire-and-forget: populate subscription, registration types & countries in Redux
      this.props.requestSubscriptionTypes().catch(() => {})
      this.props.requestRegistrationTypes().catch(() => {})
      this.props.requestCountries().catch(() => {})
    } catch (e) {
      console.error(e)
      this.setState({ failed: true })
    } finally {
      await removeLogoutSignal()
      window.addEventListener('storage', this.onStorageEvent)

      // Saves sort state to local storage before page refresh occurs
      window.addEventListener('beforeunload', this.saveSortStateToStorage)

      this.onLoad()
    }

    // Initial version check on load, then re-check whenever the tab
    // regains visibility so long-lived sessions notice new deploys without
    // a manual refresh. window.clientVersion is frozen at load time, while
    // version.json is re-fetched fresh each time.
    this.checkVersion()
    document.addEventListener('visibilitychange', this.handleVisibilityChange)
  }

  checkVersion = async () => {
    //Check if the version is different from the client version
    try {
      const noCacheHeaders = new Headers()
      noCacheHeaders.append('pragma', 'no-cache')
      noCacheHeaders.append('cache-control', 'no-cache')

      const config = {
        method: 'GET',
        headers: noCacheHeaders,
      }
      // add a cache busting query string
      const response = await fetch('/version.json?t=' + Date.now(), config)
      const json = await response.json()
      const version = json.version

      if (version && version !== window.clientVersion) {
        this.setState({ outdated: true })
      }
    } catch (e) {
      console.error('There was a problem checking app version')
    }
  }

  handleVisibilityChange = () => {
    if (document.visibilityState === 'visible') {
      this.checkVersion()
    }
  }

  componentWillUnmount() {
    window.removeEventListener('storage', this.onStorageEvent)
    window.removeEventListener('keydown', this.handleSidebarShortcut)
    document.removeEventListener(
      'visibilitychange',
      this.handleVisibilityChange
    )
  }

  checkHomeRecentsList() {
    const userId = this.props?.selfUser?.id
    try {
      const recentsString = localStorage.getItem(RECENTS_KEY_NAME)
      const recents = JSON.parse(recentsString)

      if (
        typeof userId === 'string' &&
        userId.length > 0 &&
        recents?.uid !== userId
      ) {
        localStorage.setItem(RECENTS_KEY_NAME, JSON.stringify({ uid: userId }))
      }
    } catch (e) {
      console.error('checkHomeRecentsList', e)
    }
  }

  // This runs on any app reload
  componentDidUpdate(prevProps) {
    if (this.props.isLoggedIn && !prevProps.isLoggedIn) {
      // Wipe the React Query cache on a genuine manual login so the new session
      // never serves the previous user's cached data (e.g. the /users/me/family
      // list behind useMeFamily). The false→true edge ALSO fires on a session
      // restore (componentDidMount's refresh() dispatches login.success), so we
      // gate on `!loading`: a restore reaches this edge while `loading` is still
      // true (onLoad runs later in componentDidMount's finally), whereas a manual
      // login happens after onLoad set loading=false. Skipping the restore edge
      // avoids clearing a returning user's cache on every page reload.
      if (!this.state.loading) {
        queryClient.clear()
      }
      if (typeof this.props?.selfUser?.email === 'string') {
        Sentry.setUser({ email: this.props.selfUser.email })
        if (!this.state.inFrame) {
          this.checkHomeRecentsList()
        }
      }
      if (!this.state.inFrame) {
        // We dont need to get notifications here because we are already getting them when opening the notifications page
        // this.props.getNotifications()
      }
      // // Generate a sandbox on successful login
      // req('/users/init', { parseJSON: false, noThrow: true }).catch(() =>
      //   console.log('there was a problem initing the user')
      // )
    }
  }

  changeSort = (sortState) => {
    this.setState({ sortState })
  }

  loadGA = async () => {
    const host = window.location.hostname || window.location.host
    if (
      __DEV__ ||
      !['app.sportninja.com', 'canlanstats.sportninja.com'].includes(host)
    ) {
      return
    }

    await import('../utils/ga-plugins')
    window.ga('require', 'pageVisibilityTracker')
    window.ga('require', 'urlChangeTracker')
    window.ga('send', 'pageview')
  }

  logout = () => {
    clearAllBodyScrollLocks()

    // Create one long path to match based on all private routes in the app
    const privateRoutes = `(${AUTHENTICATED_ROUTES.join('|')})`

    // Test the current path against this
    const isPrivateRoute = matchPath(this.props.history.location.pathname, {
      path: privateRoutes,
    })

    // Add a new history entry so that upon re-login, user is taken to homepage
    // However, only do so if on a private route. If user is on a page that does
    // not require login, then don't cause a redirect
    this.props.logout(isPrivateRoute !== null)
  }

  // Called either by a successful login, or by an auth refresh when returning
  // to the application
  onLoad = () => this.setState({ loading: false })

  onStorageEvent = (window[SN_STORAGE_EVENT_LISTENER] = (event) => {
    // If another tab sent a logout signal, and it wasn't to _remove_ the signal
    // then process it.
    // Remove our listener before we log out, so we don't end up in an infinite
    // loop.
    if (event.key === LOGOUT_SIGNAL && event.newValue !== null) {
      if (!this.state.inFrame) {
        this.logout()
      }
    }
  })

  renderSignUp = (props) => {
    if (this.props.isLoggedIn) {
      return <Redirect to={ROUTES.HOME} />
    }

    const { register } = this.props
    return <SignUp register={register} {...props} />
  }

  renderLogin = (props) => {
    if (!this.props.isLoggedIn) {
      return <Login login={this.props.login} {...props} />
    }

    const {
      location: { state },
    } = props
    let to = ROUTES.HOME
    if (state && state.referrer) {
      to = state.referrer
    } else if (state && state.from && state.from.pathname) {
      to = state.from.pathname
      if (state?.from?.search) {
        to += state?.from?.search
      }
    }

    return <Redirect to={to} />
  }

  renderPublicGame = (props) => {
    return <Game isPublicRoute {...props} />
  }

  renderPublicPlayer = (props) => {
    return <Player isPublicRoute {...props} />
  }

  saveSortStateToStorage = () => {
    localStorage.setItem(
      SORT_STATE_STORAGE_KEY,
      JSON.stringify(this.state.sortState)
    )
  }

  toggleSidebar = () => {
    const newCollapsed = !this.state.sidebarCollapsed
    this.setState({ sidebarCollapsed: newCollapsed }, () => {
      try {
        localStorage.setItem(
          SIDEBAR_COLLAPSED_KEY,
          JSON.stringify(newCollapsed)
        )
        // Dispatch custom event for cross-component synchronization
        dispatchSidebarToggle(newCollapsed)
      } catch (error) {
        console.error('🚨 App: Error saving sidebar state to localStorage', {
          error,
        })
      }
    })
  }

  // Handle Cmd+. (Mac) / Ctrl+. (Windows/Linux) to toggle sidebar
  handleSidebarShortcut = (event) => {
    const isMac = navigator.platform.toUpperCase().indexOf('MAC') >= 0
    const isShortcut = isMac
      ? event.metaKey && event.key === '.'
      : event.ctrlKey && event.key === '.'

    if (!isShortcut) {
      return
    }

    // Check if sidebar should be hidden on current route
    const currentPathname = this.props.history?.location?.pathname
    const shouldHideSidebar =
      currentPathname === ROUTES.SCORESHEETS ||
      currentPathname === ROUTES.SCORESHEETS_FIREFOX ||
      currentPathname === ROUTES.SIGNIN_REPORTS_PRINT ||
      currentPathname === ROUTES.SIGNIN_REPORTS_PRINT_FIREFOX ||
      currentPathname === ROUTES.REGISTRATION_PAYMENT ||
      currentPathname?.includes('/verify-email') ||
      currentPathname?.includes('/verifyEmail') ||
      currentPathname?.includes('/login') ||
      currentPathname?.includes('/signup') ||
      currentPathname?.includes('/forgot-password') ||
      currentPathname?.includes('/password-reset') ||
      currentPathname?.includes('/registration/') ||
      currentPathname?.includes('/pay') ||
      currentPathname?.includes('/ant-test')

    // Only toggle if logged in and sidebar is enabled
    if (this.props.isLoggedIn && !shouldHideSidebar) {
      event.preventDefault()
      this.toggleSidebar()
    }
  }

  render() {
    const {
      failed,
      inFrame,
      loading,
      sortState,
      outdated,
      frameConfig,
      sidebarCollapsed,
    } = this.state

    const {
      selfUser,
      forgot,
      forgotRegistration,
      history,
      isLoggedIn,
      register,
      reset,
      verify,
      verifyEmailChange,
      readUser,
      refresh,
      logout,
    } = this.props

    if (loading) {
      return (
        <Fragment>
          {inFrame && <FrameStyles />}
          <LoadingSpinner fullScreen />
        </Fragment>
      )
    }

    const sortContextValue = { sortState, changeSort: this.changeSort }
    const frameContextValue = frameConfig

    // Check if we should hide the sidebar (print routes and auth-related routes)
    const currentPathname = history?.location?.pathname
    const shouldHideSidebar =
      currentPathname === ROUTES.SCORESHEETS ||
      currentPathname === ROUTES.SCORESHEETS_FIREFOX ||
      currentPathname === ROUTES.SIGNIN_REPORTS_PRINT ||
      currentPathname === ROUTES.SIGNIN_REPORTS_PRINT_FIREFOX ||
      currentPathname === ROUTES.REGISTRATION_PAYMENT ||
      currentPathname?.includes('/verify-email') ||
      currentPathname?.includes('/verifyEmail') ||
      currentPathname?.includes('/login') ||
      currentPathname?.includes('/signup') ||
      currentPathname?.includes('/forgot-password') ||
      currentPathname?.includes('/password-reset') ||
      currentPathname?.includes('/registration/') ||
      currentPathname?.includes('/ant-test')

    return (
      <ConnectedRouter history={history}>
        <ScrollToTop>
          <FramePageListener />
          <RegistrationContextWrapper
            login={this.props.login}
            logout={this.props.logout}
            loginRegistration={this.props.loginRegistration}
            setCredentials={this.props.setCredentials}
            isLogged={this.props.isLoggedIn}
            selfUser={selfUser}
            isOnRegistration={this.state.isOnRegistration}
            genericSportProperties={this.props.genericSportProperties}
          >
            <FrameContext.Provider value={frameContextValue}>
              <SearchContextProvider isLoggedIn={isLoggedIn}>
                <TableSortContext.Provider value={sortContextValue}>
                  <PusherContextProvider isLoggedIn={isLoggedIn}>
                    <GameChannelProvider>
                      <ScheduleReprocessingContextProvider user={selfUser}>
                        <Global
                          styles={css`
                            button {
                              appearance: none;
                              border: 0;
                              padding: 0;
                              margin: 0;
                              background-color: initial;
                              outline: none;
                              cursor: pointer;
                            }

                            input,
                            select,
                            textarea {
                              appearance: none;
                              background-color: transparent;
                              outline: none;
                            }

                            li {
                              list-style: none;
                            }

                            /* Global thin scrollbar styling */
                            * {
                              scrollbar-width: thin;
                              scrollbar-color: ${colors.SECONDARY_100}
                                ${colors.SECONDARY_400};
                            }

                            *::-webkit-scrollbar {
                              width: 8px;
                              height: 8px;
                            }

                            *::-webkit-scrollbar-track {
                              background: ${colors.SECONDARY_400};
                            }

                            *::-webkit-scrollbar-thumb {
                              background: ${colors.SECONDARY_100};
                              border-radius: 4px;
                            }

                            *::-webkit-scrollbar-thumb:hover {
                              background: ${colors.NEUTRAL_100};
                            }
                          `}
                        />

                        {inFrame && <FrameStyles />}
                        <ErrorBoundary inFrame={inFrame}>
                          <Route
                            render={(props) => {
                              // The scoresheets mass printing page is purposefully devoid of
                              // the normal look and feel to more easily facilitate printing.
                              if (
                                props.location.pathname ===
                                  ROUTES.SCORESHEETS ||
                                props.location.pathname ===
                                  ROUTES.SCORESHEETS_FIREFOX ||
                                props.location.pathname ===
                                  ROUTES.SIGNIN_REPORTS_PRINT ||
                                props.location.pathname ===
                                  ROUTES.SIGNIN_REPORTS_PRINT_FIREFOX ||
                                props.location.pathname.includes('playoffs') ||
                                inFrame
                              ) {
                                return false
                              }

                              const minimal =
                                !isLoggedIn ||
                                !authenticatedRoute(props.location.pathname)
                              return (
                                <MainNav
                                  user={selfUser}
                                  minimal={minimal}
                                  onSidebarToggle={this.toggleSidebar}
                                  sidebarCollapsed={sidebarCollapsed}
                                  {...props}
                                />
                              )
                            }}
                          />

                          {failed ? (
                            <div
                              css={css`
                                padding: 40px 20px 0;
                              `}
                            >
                              {inFrame && (
                                <Global
                                  styles={css`
                                    body {
                                      color: black;
                                      background: white;
                                    }
                                  `}
                                />
                              )}
                              <h1
                                css={css`
                                  ${font.title}
                                  font-size: 32px;
                                  font-weight: bold;
                                `}
                              >
                                Something went wrong.
                              </h1>
                              <p
                                css={css`
                                  margin: 20px 0 12px;
                                `}
                              >
                                {t('Web:problemReachingServer')}
                              </p>
                            </div>
                          ) : (
                            <>
                              <ErrorController />
                              <BannerContainer />
                              <OutdatedHandler
                                inFrame={inFrame}
                                outdated={outdated}
                              />
                              {isLoggedIn && <Search userId={selfUser?.id} />}

                              {/* Wrap content with Layout structure and Sidebar */}
                              <Layout
                                style={{ minHeight: 'calc(100vh - 56px)' }} // eslint-disable-line react-native/no-inline-styles
                              >
                                {/* Only show sidebar for logged in users on authenticated routes (not on scoresheets/print routes, and not when embedded in an iframe) */}
                                {isLoggedIn &&
                                  !shouldHideSidebar &&
                                  !inFrame && (
                                    <Sidebar
                                      collapsed={sidebarCollapsed}
                                      onCollapse={(collapsed, type) => {
                                        // Ignore responsive (breakpoint-triggered) collapse if user has a saved preference
                                        if (
                                          type === 'responsive' &&
                                          this.state.hasSavedSidebarPreference
                                        ) {
                                          return
                                        }
                                        this.setState(
                                          {
                                            sidebarCollapsed: collapsed,
                                            hasSavedSidebarPreference: true,
                                          },
                                          () => {
                                            try {
                                              localStorage.setItem(
                                                SIDEBAR_COLLAPSED_KEY,
                                                JSON.stringify(collapsed)
                                              )
                                              // Dispatch custom event for cross-component synchronization
                                              dispatchSidebarToggle(collapsed)
                                            } catch (error) {
                                              console.error(
                                                '🚨 App: Error saving sidebar state',
                                                { error }
                                              )
                                            }
                                          }
                                        )
                                      }}
                                      user={selfUser}
                                    />
                                  )}

                                {/* Main content area */}
                                <Layout.Content
                                  // eslint-disable-next-line react-native/no-inline-styles
                                  style={{
                                    marginLeft:
                                      isLoggedIn &&
                                      !shouldHideSidebar &&
                                      !inFrame
                                        ? sidebarCollapsed
                                          ? 56
                                          : 284
                                        : 0,
                                    transition: 'margin-left 0.2s',
                                    backgroundColor: colors.SECONDARY_500,
                                  }}
                                >
                                  <Switch>
                                    <Redirect
                                      exact
                                      from={ROUTES.HOME}
                                      to={
                                        inFrame
                                          ? ROUTES.ORGS.ROOT
                                          : ROUTES.HOME_ROOT
                                      }
                                    />
                                    <Route
                                      path={ROUTES.PUBLIC_GAME_PAGES}
                                      render={(props) => {
                                        const gameId = props.match.params.id
                                        return (
                                          <Redirect to={`/game/${gameId}`} />
                                        )
                                      }}
                                    />
                                    <Route
                                      path={ROUTES.PUBLIC_PLAYER_PAGES}
                                      component={this.renderPublicPlayer}
                                    />
                                    <Route
                                      path={ROUTES.PLAYER_PAGES}
                                      component={Player}
                                    />
                                    <Route
                                      path={ROUTES.REGISTRATION_INVITE}
                                      // eslint-disable-next-line react/no-unstable-nested-components
                                      component={() => (
                                        <RegistrationInvite
                                          login={this.props.login}
                                          logout={this.props.logout}
                                          isLogged={this.props.isLoggedIn}
                                          genericSportProperties={
                                            this.props.genericSportProperties
                                          }
                                          playerTypes={this.props.playerTypes}
                                          sports={this.props.sports}
                                        />
                                      )}
                                    />
                                    <PrivateRoute
                                      isAuthenticated={isLoggedIn}
                                      path={ROUTES.HOME_ROOT}
                                      component={Home}
                                    />
                                    {/* <PrivateRoute
                              isAuthenticated={isLoggedIn}
                              path={'/ant-test'}
                              component={AntTestPage}
                            /> */}
                                    <PrivateRoute
                                      isAuthenticated={isLoggedIn}
                                      path={ROUTES.GENERAL_SETTINGS}
                                      component={GeneralSettings}
                                      user={selfUser}
                                    />
                                    <PrivateRoute
                                      isAuthenticated={isLoggedIn}
                                      path={ROUTES.COMING_SOON}
                                      component={ComingSoon}
                                    />
                                    <PrivateRoute
                                      isAuthenticated={isLoggedIn}
                                      path={ROUTES.COMMUNICATIONS}
                                      component={Communications}
                                    />
                                    <PrivateRoute
                                      isAuthenticated={isLoggedIn}
                                      path={ROUTES.LOCATIONS}
                                      component={Locations}
                                    />
                                    <PrivateRoute
                                      isAuthenticated={isLoggedIn}
                                      path={ROUTES.PROFILE.ROOT}
                                      component={Profile}
                                    />
                                    <Route
                                      path={ROUTES.GAME_PAGES}
                                      render={(props) => (
                                        <Game
                                          isPublicRoute={!isLoggedIn}
                                          {...props}
                                        />
                                      )}
                                    />
                                    <PrivateRoute
                                      isAuthenticated={isLoggedIn}
                                      path={ROUTES.TEAMS.ROOT}
                                      component={Teams}
                                    />
                                    <PrivateRoute
                                      isAuthenticated={isLoggedIn}
                                      path={ROUTES.TEAM_PAGES}
                                      component={Team}
                                    />
                                    <PrivateRoute
                                      isAuthenticated={isLoggedIn}
                                      path={ROUTES.ORGS.ROOT}
                                      component={Organizations}
                                    />
                                    <PrivateRoute
                                      isAuthenticated={isLoggedIn}
                                      path={ROUTES.REGISTRATIONS.ROOT}
                                      component={Registrations}
                                    />
                                    <PrivateRoute
                                      isAuthenticated={isLoggedIn}
                                      path={ROUTES.MY_SCHEDULE}
                                      component={MySchedule}
                                    />
                                    <PrivateRoute
                                      isAuthenticated={isLoggedIn}
                                      path={ROUTES.MY_MEMBERSHIP}
                                      component={MyMembership}
                                    />
                                    <PrivateRoute
                                      isAuthenticated={isLoggedIn}
                                      path={ROUTES.FOLLOWING.ROOT}
                                      component={Following}
                                    />
                                    <PrivateRoute
                                      isAuthenticated={isLoggedIn}
                                      path={ROUTES.ORG_PAGES}
                                      component={Org}
                                    />
                                    <PrivateRoute
                                      isAuthenticated={isLoggedIn}
                                      path={ROUTES.SCHEDULES.ROOT}
                                      component={Schedules}
                                    />
                                    <PrivateRoute
                                      isAuthenticated={isLoggedIn}
                                      path={ROUTES.SCHEDULE_PAGES}
                                      component={Schedule}
                                    />
                                    <PrivateRoute
                                      isAuthenticated={isLoggedIn}
                                      path={ROUTES.REGISTRATION_PAGES}
                                      component={RegistrationDetails}
                                    />
                                    <PrivateRoute
                                      isAuthenticated={isLoggedIn}
                                      path={ROUTES.REGISTRATION_PREVIEW}
                                      component={RegistrationPreview}
                                      genericSportProperties={
                                        this.props.genericSportProperties
                                      }
                                      playerTypes={this.props.playerTypes}
                                      sports={this.props.sports}
                                    />
                                    <PrivateRoute
                                      isAuthenticated={isLoggedIn}
                                      path={ROUTES.HIGHLIGHT_SHARE}
                                      component={HighlightRedirect}
                                    />
                                    <PrivateRoute
                                      isAuthenticated={isLoggedIn}
                                      path={ROUTES.SCORESHEETS}
                                      component={Scoresheets}
                                    />
                                    <PrivateRoute
                                      isAuthenticated={isLoggedIn}
                                      path={ROUTES.SCORESHEETS_FIREFOX}
                                      component={ScoresheetsFirefox}
                                    />
                                    <PrivateRoute
                                      isAuthenticated={isLoggedIn}
                                      path={ROUTES.SIGNIN_REPORTS_PRINT}
                                      component={SignInReportsPrint}
                                    />
                                    <PrivateRoute
                                      isAuthenticated={isLoggedIn}
                                      path={ROUTES.SIGNIN_REPORTS_PRINT_FIREFOX}
                                      component={SignInReportsPrintFirefox}
                                    />
                                    <PrivateRoute
                                      isAuthenticated={isLoggedIn}
                                      path={ROUTES.MERGE_PLAYERS}
                                      component={MergePlayers}
                                    />
                                    <PrivateRoute
                                      isAuthenticated={isLoggedIn}
                                      path={ROUTES.MERGE_TEAMS}
                                      component={MergeTeams}
                                    />
                                    <PrivateRoute
                                      isAuthenticated={isLoggedIn}
                                      exact
                                      path={ROUTES.INVITE_V2}
                                      component={InviteV2}
                                    />
                                    <PrivateRoute
                                      isAuthenticated={isLoggedIn}
                                      exact
                                      path={ROUTES.REGISTRATION_PAYMENT}
                                      component={RegistrationPayment}
                                      preserveQuery={true}
                                    />
                                    <Route
                                      exact
                                      path={ROUTES.INVITE_REGISTER}
                                      component={InviteRegister}
                                    />
                                    <Route
                                      exact
                                      path={ROUTES.SIGNUP_REGISTER}
                                      component={InviteRegister}
                                    />
                                    <Route
                                      exact
                                      path={ROUTES.SIGNUP}
                                      render={this.renderSignUp}
                                    />
                                    <Route
                                      exact
                                      path={ROUTES.REGISTER}
                                      render={this.renderSignUp}
                                    />
                                    <Route
                                      exact
                                      path={ROUTES.REGISTER_SUCCESS}
                                      component={RegisterSuccess}
                                    />
                                    <Route
                                      exact
                                      path={ROUTES.FORGOT_PASSWORD}
                                      render={(props) => (
                                        <ForgotPassword
                                          forgot={forgot}
                                          forgotRegistration={
                                            forgotRegistration
                                          }
                                          {...props}
                                        />
                                      )}
                                    />
                                    <Route
                                      exact
                                      path={ROUTES.SIGNUP_SUCCESS}
                                      component={RegisterSuccess}
                                    />
                                    <Route
                                      exact
                                      path={ROUTES.LOGIN}
                                      render={this.renderLogin}
                                    />
                                    <Route
                                      exact
                                      path={ROUTES.VERIFY_EMAIL}
                                      render={(props) => (
                                        <VerifyEmail
                                          refresh={refresh}
                                          verify={verify}
                                          readUser={readUser}
                                          logout={logout}
                                          {...props}
                                        />
                                      )}
                                    />
                                    <Route
                                      exact
                                      path={ROUTES.VERIFY_EMAIL_CHANGE}
                                      render={(props) => (
                                        <VerifyEmail
                                          refresh={refresh}
                                          verify={verifyEmailChange}
                                          readUser={readUser}
                                          isEmailChange={true}
                                          logout={logout}
                                          {...props}
                                        />
                                      )}
                                    />
                                    <Route
                                      exact
                                      path={ROUTES.VERIFY_EMAIL_2}
                                      render={(props) => (
                                        <VerifyEmail
                                          verify={verify}
                                          refresh={refresh}
                                          readUser={readUser}
                                          isEmailChange={false}
                                          logout={logout}
                                          {...props}
                                        />
                                      )}
                                    />
                                    <Route
                                      exact
                                      path={ROUTES.PASSWORD_RESET}
                                      render={(props) => (
                                        <ResetPassword
                                          reset={reset}
                                          {...props}
                                        />
                                      )}
                                    />
                                    <Route
                                      exact
                                      path={ROUTES.PASSWORD_RESET_REGISTRATION}
                                      render={(props) => (
                                        <ResetPassword
                                          reset={reset}
                                          isRegistration={true}
                                          {...props}
                                        />
                                      )}
                                    />
                                    <Route
                                      exact
                                      path={ROUTES.PLAYOFFS_MOBILE}
                                      render={(props) => (
                                        <PlayoffMobile {...props} />
                                      )}
                                    />
                                    {/* <Route exact path='/test' component={Test} /> */}
                                    {/* New Sandbox Prepare Route */}
                                    <PrivateRoute
                                      isAuthenticated={isLoggedIn}
                                      path={ROUTES.SANDBOX}
                                      component={Sandbox}
                                    />
                                    <PrivateRoute
                                      isAuthenticated={isLoggedIn}
                                      component={NoMatch}
                                    />
                                  </Switch>
                                </Layout.Content>
                              </Layout>
                            </>
                          )}
                        </ErrorBoundary>
                      </ScheduleReprocessingContextProvider>
                    </GameChannelProvider>
                  </PusherContextProvider>
                </TableSortContext.Provider>
              </SearchContextProvider>
            </FrameContext.Provider>
          </RegistrationContextWrapper>
        </ScrollToTop>
        {!inFrame &&
        !this.state.isOnRegistration &&
        !this.state.isOnPlayoffMobile &&
        !this.state.isOnTeam &&
        !this.state.isOnOrg &&
        !this.state.isOnSchedule ? (
          <DownloadApp isLoggedIn={isLoggedIn} />
        ) : null}
      </ConnectedRouter>
    )
  }
}

App.propTypes = {
  // Parent
  history: PropTypes.object.isRequired,

  // Redux
  selfUser: PropTypes.object,
  isLoggedIn: PropTypes.bool,

  // Dispatches
  forgot: PropTypes.func.isRequired,
  location: PropTypes.object,
  login: PropTypes.func.isRequired,
  logout: PropTypes.func.isRequired,
  refresh: PropTypes.func.isRequired,
  register: PropTypes.func.isRequired,
  requestSports: PropTypes.func.isRequired,
  requestTypesBySport: PropTypes.func.isRequired,
  reset: PropTypes.func.isRequired,
  verify: PropTypes.func.isRequired,
}

const mapStateToProps = (state) => {
  return {
    selfUser: getSelfUser(state),
    isLoggedIn: state.auth.login.success,
    genericSportProperties: getAllGenericSportProperties(state),
  }
}

const mapDispatchToProps = (dispatch) => {
  return {
    forgot: bindActionToPromise(dispatch, authActions.forgot.request),
    forgotRegistration: bindActionToPromise(
      dispatch,
      authActions.forgot_registration.request
    ),
    login: bindActionToPromise(dispatch, authActions.login.request),
    loginRegistration: bindActionToPromise(
      dispatch,
      authActions.loginRegistration.request
    ),
    setCredentials: bindActionToPromise(
      dispatch,
      authActions.setCredentials.request
    ),
    logout: bindActionToPromise(dispatch, authActions.logout.request),
    refresh: bindActionToPromise(dispatch, authActions.refresh.request),
    register: bindActionToPromise(dispatch, authActions.new_register.request),
    getNotifications: bindActionToPromise(
      dispatch,
      notificationsActions.listAll.request
    ),
    requestSports: bindActionToPromise(dispatch, typeActions.sports.request),
    requestTypesBySport: bindActionToPromise(
      dispatch,
      typeActions.typesBySport.request
    ),
    requestTypesAllSports: bindActionToPromise(
      dispatch,
      typeActions.allTypes.request
    ),
    requestSubscriptionTypes: bindActionToPromise(
      dispatch,
      typeActions.subscriptionTypes.request
    ),
    requestRegistrationTypes: bindActionToPromise(
      dispatch,
      typeActions.registrationTypes.request
    ),
    requestCountries: bindActionToPromise(
      dispatch,
      typeActions.countries.request
    ),
    reset: bindActionToPromise(dispatch, authActions.reset.request),
    setInFrame: (payload) => dispatch({ type: 'IN_FRAME', payload }),
    dispatchSetInviteSports: (sports) => dispatch(setInviteSports(sports)),
    verify: bindActionToPromise(dispatch, authActions.verify.request),
    verifyEmailChange: bindActionToPromise(
      dispatch,
      authActions.verifyEmailChange.request
    ),
    readUser: bindActionToPromise(dispatch, userActions.read.request),
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(App)
